import { typeof as _typeof, slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useBlockEnvironment } from '@zillow/cms-blocks/BlockEnvironment';
import { Box, Spacer, FormField, Label, Text, FormHelp, Textarea, Alert, LoadingButton } from '@zillow/constellation';
import { cookiesToZGUID } from '@zillow/mortgage-urls';
import React, { useRef, useEffect } from 'react';
import { useForm, useFormState, FormProvider } from 'react-hook-form';
import { useCurrentUser } from '../../hooks/user.js';
import EmailField from '../Fields/EmailField.js';
import NameField from '../Fields/NameField.js';
import TextField from '../Fields/TextField.js';
import AddressAutocompleteField from './AddressAutocompleteField.js';
import SuccessState from './SuccessState.js';
import API_ENDPOINTS from './constants/api.js';
import { triggerFormSubmitClickstreamEvent, triggerPrivacyPolicyClickstreamEvent, triggerFormFieldEngagementClickstreamEvent, triggerSubmitAnotherReportClickstreamEvent } from './utils/clickstream.js';
import { ifNullStringNull } from './utils/common.js';

var API_ERROR_MESSAGE = 'There was an error submitting the form. Please try again.';
function scrollToTop(ref) {
  setTimeout(function () {
    var _ref$current;
    (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.scrollIntoView({
      behavior: 'smooth'
    });
  }, 0);
}
function triggerFailedSubmit(invalidFields) {
  if (invalidFields === void 0) {
    invalidFields = [];
  }
  triggerFormSubmitClickstreamEvent(false, invalidFields, false, '');
}
var PropertyReportTransparency = function PropertyReportTransparency(_ref) {
  var _API_ENDPOINTS$enviro, _errors$address, _errors$comments;
  var id = _ref.id;
  var _useBlockEnvironment = useBlockEnvironment(),
    _useBlockEnvironment$ = _useBlockEnvironment.environment,
    environment = _useBlockEnvironment$ === void 0 ? 'prod' : _useBlockEnvironment$;
  var apiEndpoint = (API_ENDPOINTS === null || API_ENDPOINTS === void 0 || (_API_ENDPOINTS$enviro = API_ENDPOINTS[environment]) === null || _API_ENDPOINTS$enviro === void 0 ? void 0 : _API_ENDPOINTS$enviro.LISTING_CONTACT) || API_ENDPOINTS.prod.LISTING_CONTACT;
  var methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      address: '',
      comments: '',
      mlsName: '',
      userName: '',
      userEmail: ''
    }
  });
  var _useFormState = useFormState({
      control: methods.control
    }),
    errors = _useFormState.errors;

  // Prefill user info if logged in
  var _useCurrentUser = useCurrentUser(environment === 'prod' ? 'production' : 'qa'),
    data = _useCurrentUser.data;
  var user = data !== null && data !== void 0 && data.user && _typeof(data.user) === 'object' ? data.user : null;

  // Store zpid and encodedZuid outside of form state
  var _React$useState = React.useState(''),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    zpid = _React$useState2[0],
    setZpid = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    isSuccess = _React$useState4[0],
    setIsSuccess = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    isSubmitting = _React$useState6[0],
    setIsSubmitting = _React$useState6[1];
  var _React$useState7 = React.useState(''),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    referenceNumber = _React$useState8[0],
    setReferenceNumber = _React$useState8[1];
  var _React$useState9 = React.useState(''),
    _React$useState0 = _slicedToArray(_React$useState9, 2),
    apiError = _React$useState0[0],
    setApiError = _React$useState0[1];
  var containerRef = useRef(null);
  useEffect(function () {
    if (user) {
      var userName = [user.firstName, user.lastName].filter(Boolean).join(' ');
      methods.setValue('userName', userName);
      methods.setValue('userEmail', user.email);
    }
  }, [methods, user]);
  var handleAddressSelect = function handleAddressSelect(address, zpidValue) {
    methods.setValue('address', address, {
      shouldValidate: true
    });
    if (zpidValue) {
      setZpid(zpidValue);
    }
  };
  var handleBlur = function handleBlur(label) {
    return function () {
      triggerFormFieldEngagementClickstreamEvent(label);
    };
  };
  var handleSubmitAnotherReport = function handleSubmitAnotherReport() {
    triggerSubmitAnotherReportClickstreamEvent('Submit another report', '', '_self');
    setIsSuccess(false);
    setReferenceNumber('');
    // Reset form to empty values
    methods.reset({
      address: '',
      comments: '',
      mlsName: '',
      userName: methods.getValues('userName'),
      userEmail: methods.getValues('userEmail')
    });
    setZpid('');
    scrollToTop(containerRef);
  };
  var onSubmit = methods.handleSubmit(function (data) {
    return new Promise(function ($return, $error) {
      var encodedZuid, formData, response, _data$message, _data;
      setApiError('');
      setIsSubmitting(true);
      encodedZuid = ifNullStringNull(cookiesToZGUID(document.cookie)) || '';
      formData = Object.assign({}, data, {
        zpid: zpid,
        encodedZuid: encodedZuid
      });
      var $Try_1_Post = function () {
        try {
          setIsSubmitting(false);
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };
      var $Try_1_Catch = function (e) {
        try {
          setApiError(API_ERROR_MESSAGE);
          triggerFailedSubmit();
          return $Try_1_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };
      try {
        return Promise.resolve(fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        })).then(function ($await_3) {
          try {
            response = $await_3;
            if (response.ok) {
              return Promise.resolve(response.json()).then(function ($await_4) {
                try {
                  _data = $await_4;
                  if ((_data === null || _data === void 0 || (_data$message = _data.message) === null || _data$message === void 0 ? void 0 : _data$message.code) === 200) {
                    setReferenceNumber(_data === null || _data === void 0 ? void 0 : _data.key);
                    triggerFormSubmitClickstreamEvent(true, [], true, '');
                    setIsSuccess(true);
                    setApiError('');
                    scrollToTop(containerRef);
                  } else {
                    setApiError(API_ERROR_MESSAGE);
                    triggerFailedSubmit();
                  }
                  return $If_2.call(this);
                } catch ($boundEx) {
                  return $Try_1_Catch($boundEx);
                }
              }.bind(this), $Try_1_Catch);
            } else {
              setApiError(API_ERROR_MESSAGE);
              triggerFailedSubmit();
              return $If_2.call(this);
            }
            function $If_2() {
              return $Try_1_Post();
            }
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }.bind(this), $Try_1_Catch);
      } catch (e) {
        $Try_1_Catch();
      }
    });
  }, function (errors) {
    // On invalid submit
    var invalidFields = Object.keys(errors);
    triggerFailedSubmit(invalidFields);
    setIsSubmitting(false);
  });
  if (isSuccess) {
    return /*#__PURE__*/React.createElement(SuccessState, {
      referenceNumber: referenceNumber,
      onSubmitAnother: handleSubmitAnotherReport
    });
  }
  return /*#__PURE__*/React.createElement(FormProvider, methods, /*#__PURE__*/React.createElement(Box, {
    id: id,
    ref: containerRef
  }, /*#__PURE__*/React.createElement(AddressAutocompleteField, {
    onSelect: handleAddressSelect,
    required: true,
    label: "Property address",
    error: errors === null || errors === void 0 || (_errors$address = errors.address) === null || _errors$address === void 0 ? void 0 : _errors$address.message,
    placeholder: "Enter complete property address with street, city, state, and ZIP code",
    description: "Enter the complete property address for the listing with the violation.",
    onBlur: handleBlur('Property address')
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }), /*#__PURE__*/React.createElement(Box, {
    as: "form",
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, methods.register('address', {
    required: {
      value: true,
      message: 'Property address is required'
    }
  }))), /*#__PURE__*/React.createElement(FormField, {
    label: /*#__PURE__*/React.createElement(Label, {
      htmlFor: "comments"
    }, "Report details", ' ', /*#__PURE__*/React.createElement(Text, {
      fontColor: "textLight",
      fontType: "bodySmall"
    }, "(optional)")),
    description: /*#__PURE__*/React.createElement(FormHelp, {
      error: !!errors.comments
    }, ((_errors$comments = errors.comments) === null || _errors$comments === void 0 ? void 0 : _errors$comments.message) || 'Include any details about the listing or the violation that would be helpful.'),
    control: /*#__PURE__*/React.createElement(Textarea, _extends({
      id: "comments",
      placeholder: "Any information that might help our investigation, including a web address or URL if relevant.",
      rows: "4"
    }, methods.register('comments', {
      onBlur: handleBlur('Report details')
    })))
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }), /*#__PURE__*/React.createElement(NameField, {
    name: "userName",
    label: "Your name",
    required: true,
    placeholder: "Enter your full name",
    onBlur: handleBlur('Your name')
  }), /*#__PURE__*/React.createElement(EmailField, {
    name: "userEmail",
    label: "Your email",
    required: true,
    placeholder: "your.email@example.com",
    onBlur: handleBlur('Your email')
  }), /*#__PURE__*/React.createElement(TextField, {
    name: "mlsName",
    label: /*#__PURE__*/React.createElement(React.Fragment, null, "MLS name", ' ', /*#__PURE__*/React.createElement(Text, {
      fontColor: "textLight",
      fontType: "bodySmall"
    }, "(optional)")),
    placeholder: "Enter MLS name",
    onBlur: handleBlur('MLS name')
  }), /*#__PURE__*/React.createElement(Text, {
    as: "p",
    fontType: "legal"
  }, "We respect your privacy\u2014your personal information will only be used to follow up on this report and will not be sold or shared with third parties. The information you provide will help us enforce Zillow's listing access standards. By submitting your contact information, you agree that Zillow Group may contact you to follow up or verify the details you provided. Submitting this form does not create a contractual relationship or guarantee a response. For more information, please review our", ' ', /*#__PURE__*/React.createElement("a", {
    href: "https://www.zillow.com/privacy/",
    target: "_blank",
    rel: "noopener noreferrer",
    onClick: function onClick() {
      return triggerPrivacyPolicyClickstreamEvent('Privacy Policy', 'https://www.zillow.com/privacy/', '_blank');
    }
  }, "Privacy Policy"), "."), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "md"
  }), apiError && /*#__PURE__*/React.createElement(Box, {
    marginBottom: "sm"
  }, /*#__PURE__*/React.createElement(Alert, {
    appearance: "error",
    body: apiError
  })), /*#__PURE__*/React.createElement(LoadingButton, {
    type: "submit",
    buttonType: "primary",
    loading: isSubmitting,
    disabled: isSubmitting
  }, "Submit report"))));
};
var PropertyReportTransparency$1 = PropertyReportTransparency;

export { PropertyReportTransparency$1 as default };
